/**
 * CSS Syntax Highlighter
 * Provides syntax highlighting for CSS code
 */

import { isValidColor, parseColor, toRgbaString } from './color-detector';

export interface CSSHighlightToken {
  type: 'selector' | 'selector-class' | 'selector-id' | 'selector-element' | 'selector-pseudo' | 'selector-attribute' | 'property' | 'value' | 'color' | 'number' | 'string' | 'comment' | 'punctuation' | 'text';
  value: string;
  colorPreview?: string; // For color values
}

/**
 * Tokenize CSS code into syntax tokens
 */
export function tokenizeCSS(css: string): CSSHighlightToken[] {
  const tokens: CSSHighlightToken[] = [];
  let i = 0;

  while (i < css.length) {
    // Skip whitespace and newlines
    if (/\s/.test(css[i])) {
      tokens.push({ type: 'text', value: css[i] });
      i++;
      continue;
    }

    // Check for comments
    if (css.substring(i, i + 2) === '/*') {
      const endIndex = css.indexOf('*/', i + 2);
      if (endIndex !== -1) {
        tokens.push({
          type: 'comment',
          value: css.substring(i, endIndex + 2),
        });
        i = endIndex + 2;
        continue;
      }
    }

    // Check for strings
    if (css[i] === '"' || css[i] === "'") {
      const quote = css[i];
      let j = i + 1;
      while (j < css.length && css[j] !== quote) {
        if (css[j] === '\\') j++; // Skip escaped characters
        j++;
      }
      tokens.push({
        type: 'string',
        value: css.substring(i, j + 1),
      });
      i = j + 1;
      continue;
    }

    // Check for punctuation
    if ('{}:;,'.includes(css[i])) {
      tokens.push({ type: 'punctuation', value: css[i] });
      i++;
      continue;
    }

    // Check for selector or property-value
    const context = getContext(tokens);

    if (context === 'selector') {
      // Parse selector with enhanced token types

      // Class selector (.class-name)
      if (css[i] === '.') {
        const classMatch = css.substring(i).match(/^\.[a-zA-Z_-][a-zA-Z0-9_-]*/);
        if (classMatch) {
          tokens.push({
            type: 'selector-class',
            value: classMatch[0],
          });
          i += classMatch[0].length;
          continue;
        }
      }

      // ID selector (#id-name)
      if (css[i] === '#') {
        const idMatch = css.substring(i).match(/^#[a-zA-Z_-][a-zA-Z0-9_-]*/);
        if (idMatch) {
          tokens.push({
            type: 'selector-id',
            value: idMatch[0],
          });
          i += idMatch[0].length;
          continue;
        }
      }

      // Pseudo-class or pseudo-element (:hover, ::before)
      if (css[i] === ':') {
        const pseudoMatch = css.substring(i).match(/^::?[a-zA-Z-]+(?:\([^)]*\))?/);
        if (pseudoMatch) {
          tokens.push({
            type: 'selector-pseudo',
            value: pseudoMatch[0],
          });
          i += pseudoMatch[0].length;
          continue;
        }
      }

      // Attribute selector ([type="text"])
      if (css[i] === '[') {
        const attrMatch = css.substring(i).match(/^\[[^\]]+\]/);
        if (attrMatch) {
          tokens.push({
            type: 'selector-attribute',
            value: attrMatch[0],
          });
          i += attrMatch[0].length;
          continue;
        }
      }

      // Combinators and universal selector
      if (/[>+~*]/.test(css[i])) {
        tokens.push({
          type: 'punctuation',
          value: css[i],
        });
        i++;
        continue;
      }

      // Element selector (div, span, body, etc.)
      const elementMatch = css.substring(i).match(/^[a-zA-Z][a-zA-Z0-9-]*/);
      if (elementMatch) {
        tokens.push({
          type: 'selector-element',
          value: elementMatch[0],
        });
        i += elementMatch[0].length;
        continue;
      }
    } else if (context === 'property') {
      // Parse CSS property
      const propertyMatch = css.substring(i).match(/^[a-zA-Z-]+/);
      if (propertyMatch) {
        tokens.push({
          type: 'property',
          value: propertyMatch[0],
        });
        i += propertyMatch[0].length;
        continue;
      }
    } else if (context === 'value') {
      // Parse value (could be color, number, or regular value)
      const valueMatch = css.substring(i).match(/^[^\s;,{}]+/);
      if (valueMatch) {
        const value = valueMatch[0];

        // Check if it's a color
        if (isValidColor(value)) {
          const parsed = parseColor(value);
          tokens.push({
            type: 'color',
            value,
            colorPreview: parsed ? toRgbaString(parsed) : undefined,
          });
        }
        // Check if it's a number with unit
        else if (/^-?[\d.]+([a-z%]+)?$/i.test(value)) {
          tokens.push({
            type: 'number',
            value,
          });
        }
        // Regular value
        else {
          tokens.push({
            type: 'value',
            value,
          });
        }

        i += value.length;
        continue;
      }
    }

    // Fallback: treat as text
    tokens.push({ type: 'text', value: css[i] });
    i++;
  }

  return tokens;
}

/**
 * Determine context based on previous tokens
 */
function getContext(tokens: CSSHighlightToken[]): 'selector' | 'property' | 'value' {
  // Work backwards through tokens
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];

    if (token.type === 'text' && /\s/.test(token.value)) {
      continue; // Skip whitespace
    }

    // After { we're looking for properties
    if (token.type === 'punctuation' && token.value === '{') {
      return 'property';
    }

    // After : we're looking for values
    if (token.type === 'punctuation' && token.value === ':') {
      return 'value';
    }

    // After ; we're back to properties
    if (token.type === 'punctuation' && token.value === ';') {
      return 'property';
    }

    // After } we're back to selectors
    if (token.type === 'punctuation' && token.value === '}') {
      return 'selector';
    }

    // If we see a property, we're in property context
    if (token.type === 'property') {
      return 'property';
    }
  }

  // Default to selector at start
  return 'selector';
}

/**
 * Convert tokens to HTML with syntax highlighting classes
 */
export function tokensToHTML(tokens: CSSHighlightToken[]): string {
  return tokens
    .map((token) => {
      const escapedValue = escapeHtml(token.value);

      // Add color swatch for color values
      if (token.type === 'color' && token.colorPreview) {
        return `<span class="color-value-wrapper"><span class="color-swatch" style="background: ${token.colorPreview}"></span><span class="syntax-${token.type}">${escapedValue}</span></span>`;
      }

      return `<span class="syntax-${token.type}">${escapedValue}</span>`;
    })
    .join('');
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Apply syntax highlighting to CSS code
 */
export function highlightCSS(css: string): string {
  const tokens = tokenizeCSS(css);
  return tokensToHTML(tokens);
}
